import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, FREQUENCY_LABEL, WASTE_LABEL, VISIT_PREFERENCE_LABEL, SERVICES, photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import StatusBadge from "../components/StatusBadge";
import { MagnifyingGlass, CheckCircle, XCircle, CalendarBlank, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientStatus() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes/by-email`, { params: { email: email.toLowerCase().trim() } });
      setQuotes(data);
      setParams({ email: email.toLowerCase().trim() });
    } catch (e) {
      toast.error("Couldn't load your quotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("email") && quotes === null) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = lookup;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 hs-fade" data-testid="client-status-page">
      <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight">My Quote</h1>
      <p className="mt-2 text-[#73777B]">Enter the email you used to request a quote.</p>

      <form onSubmit={lookup} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="lookup-form">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-11"
          data-testid="lookup-email-input"
        />
        <Button type="submit" disabled={loading} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid="lookup-submit">
          <MagnifyingGlass size={16} weight="bold" className="mr-1.5" />
          {loading ? "Looking…" : "Look up"}
        </Button>
      </form>

      <div className="mt-8 space-y-5">
        {quotes && quotes.length === 0 && (
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-8 text-center" data-testid="no-quotes-found">
            <p className="text-[#73777B]">We couldn't find any quote requests for that email.</p>
            <Button onClick={() => nav("/quote")} className="mt-5 rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid="no-quotes-cta">
              Request a quote
            </Button>
          </div>
        )}
        {quotes && quotes.map((q) => (
          <QuoteCard key={q.id} quote={q} onChange={refresh} />
        ))}
      </div>
    </div>
  );
}

function QuoteCard({ quote, onChange }) {
  const [showDecline, setShowDecline] = useState(false);
  const [declineMsg, setDeclineMsg] = useState("");
  const [showRequest, setShowRequest] = useState(false);
  const [requestDate, setRequestDate] = useState("");
  const [requestMsg, setRequestMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [chosenKey, setChosenKey] = useState(null);

  // Open the request form automatically if email link said so
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#request-specific-date") {
      setShowRequest(true);
    }
  }, []);

  const accept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/accept`);
      toast.success("Quote accepted — we'll send you a few dates to choose from.");
      onChange();
    } catch { toast.error("Couldn't accept the quote."); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/decline`, { message: declineMsg });
      toast.success("Sent. We'll be in touch.");
      onChange();
    } catch { toast.error("Couldn't submit your reply."); }
    finally { setBusy(false); setShowDecline(false); }
  };

  const book = async () => {
    if (!chosenKey) { toast.error("Please choose a date."); return; }
    const [date, slot] = chosenKey.split("__");
    setBusy(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/pick-date`, { date, slot });
      toast.success("Booked! See you then.");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't book that date."); }
    finally { setBusy(false); }
  };

  const requestSpecific = async () => {
    if (!requestDate && !requestMsg.trim()) { toast.error("Tell us a preferred date or a quick note."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/request-specific-date`, { date: requestDate, message: requestMsg });
      toast.success("Sent! Honeystone will send you new dates shortly.");
      setShowRequest(false); setRequestDate(""); setRequestMsg("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send your request."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`quote-card-${quote.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs text-[#73777B]">Request ID</p>
          <p className="font-mono text-xs text-[#2C3E35]">{quote.id.slice(0, 8)}…</p>
        </div>
        <StatusBadge status={quote.status} />
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
        <Field label="Name" value={quote.name} />
        <Field label="Frequency" value={FREQUENCY_LABEL[quote.frequency]} />
        <Field label="Address" value={quote.address} />
        <Field label="Waste" value={WASTE_LABEL[quote.waste_disposal]} />
      </div>
      <div className="mt-3">
        <Field label="Visit preference" value={VISIT_PREFERENCE_LABEL[quote.visit_preference]} />
      </div>
      <div className="mt-3">
        <Field label="Services" value={SERVICES.filter((s) => quote.services?.includes(s.id)).map((s) => s.label).join(", ") + (quote.other_service ? ` — ${quote.other_service}` : "")} />
      </div>
      {quote.notes && <div className="mt-3"><Field label="Notes" value={quote.notes} /></div>}

      {quote.photos?.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#73777B] mb-2">Photos</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {quote.photos.map((p) => (
              <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-[#E5E0D8]">
                <img src={photoUrl(p.id)} alt="garden" className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      {quote.status === "quote_ready" && (
        <div className="mt-6 rounded-xl border border-[#D6E2CB] bg-[#F4F8F1] p-5" data-testid="quote-ready-block">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#3C5A3F]">Your quote</p>
          <p className="mt-1 font-[Outfit] text-4xl font-semibold text-[#2C3E35]">£{Number(quote.price).toFixed(2)}</p>
          {quote.breakdown && (
            <div className="mt-3 text-sm text-[#2C3E35] whitespace-pre-wrap"><span className="font-semibold">Breakdown:</span> {quote.breakdown}</div>
          )}
          {quote.admin_notes && (
            <div className="mt-2 text-sm text-[#2C3E35] whitespace-pre-wrap"><span className="font-semibold">Notes:</span> {quote.admin_notes}</div>
          )}
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button onClick={accept} disabled={busy} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid={`accept-button-${quote.id}`}>
              <CheckCircle size={18} weight="fill" className="mr-1.5" /> Accept quote
            </Button>
            <Button onClick={() => setShowDecline((v) => !v)} variant="outline" className="rounded-full h-11 px-6" data-testid={`decline-button-${quote.id}`}>
              <XCircle size={18} weight="fill" className="mr-1.5" /> Decline / Query
            </Button>
          </div>
          {showDecline && (
            <div className="mt-4 space-y-3" data-testid={`decline-block-${quote.id}`}>
              <Label htmlFor="dm">Tell us what's not quite right (optional)</Label>
              <Textarea id="dm" rows={3} value={declineMsg} onChange={(e) => setDeclineMsg(e.target.value)} placeholder="Anything we should reconsider?" data-testid={`decline-message-${quote.id}`} />
              <Button onClick={decline} disabled={busy} className="rounded-full h-10 px-5 bg-[#2C3E35] text-white hover:bg-black" data-testid={`decline-send-${quote.id}`}>Send</Button>
            </div>
          )}
        </div>
      )}

      {quote.status === "accepted" && (
        <div className="mt-6 rounded-xl border border-dashed border-[#E5E0D8] bg-[#F9F8F6] p-5 text-sm text-[#73777B]" data-testid="awaiting-dates-block">
          <p className="font-semibold text-[#2C3E35]">Thanks for accepting!</p>
          <p className="mt-1">Honeystone is putting together a few dates that work for them. We'll email you as soon as they're ready — or pop back here in a bit.</p>
        </div>
      )}

      {quote.status === "dates_offered" && (
        <div className="mt-6" data-testid="dates-offered-block" id="request-specific-date">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#73777B] mb-3 flex items-center gap-1.5">
            <CalendarBlank size={14} weight="fill" /> Choose a date that suits you
          </p>
          {(!quote.offered_dates || quote.offered_dates.length === 0) ? (
            <div className="rounded-xl border border-dashed border-[#E5E0D8] bg-[#F9F8F6] p-5 text-sm text-[#73777B]">
              Honeystone hasn't offered any dates yet — check back shortly.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quote.offered_dates.map((s) => {
                  const key = `${s.date}__${s.slot}`;
                  const active = chosenKey === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setChosenKey(key)}
                      className={`rounded-xl border p-3 text-left transition-colors ${active ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8] hover:border-[#86A789]"}`}
                      data-testid={`offered-date-${key}`}
                    >
                      <p className="text-sm font-semibold text-[#2C3E35]">{formatDate(s.date)}</p>
                      <p className="text-xs text-[#73777B] capitalize">{s.slot}</p>
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 text-sm text-[#2C3E35]">
                Please choose from our next available dates. If none are convenient, please{" "}
                <button onClick={() => setShowRequest((v) => !v)} className="font-semibold text-[#4F6F52] underline decoration-[#86A789] underline-offset-2" data-testid={`request-specific-toggle-${quote.id}`}>
                  click here to request a specific date
                </button>{" "}
                and we will do our best to accommodate you.
              </p>
              {quote.offered_expires_at && (
                <p className="mt-2 text-xs text-[#7A5A12] bg-[#F1ECDC] border border-[#E0D6B0] rounded-md inline-block px-2 py-1" data-testid="hold-notice">
                  We're holding these dates for 3 hours from when we sent them.
                </p>
              )}
              <Button onClick={book} disabled={busy || !chosenKey} className="mt-5 rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid={`book-slot-button-${quote.id}`}>
                Confirm booking <ArrowRight size={16} weight="bold" className="ml-1.5" />
              </Button>
            </>
          )}

          {showRequest && (
            <div className="mt-5 rounded-xl border border-[#E5E0D8] bg-[#F9F8F6] p-5 space-y-3" data-testid={`request-specific-block-${quote.id}`}>
              <p className="text-sm font-semibold text-[#2C3E35]">Request a specific date</p>
              <div>
                <Label htmlFor={`rd-${quote.id}`}>Your preferred date</Label>
                <Input id={`rd-${quote.id}`} type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} className="mt-1.5" data-testid={`request-date-input-${quote.id}`} />
              </div>
              <div>
                <Label htmlFor={`rm-${quote.id}`}>Anything we should know? (optional)</Label>
                <Textarea id={`rm-${quote.id}`} rows={3} value={requestMsg} onChange={(e) => setRequestMsg(e.target.value)} placeholder="e.g. mornings only, or specific weekday" data-testid={`request-message-input-${quote.id}`} />
              </div>
              <div className="flex gap-2">
                <Button onClick={requestSpecific} disabled={busy} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-10 px-5" data-testid={`request-specific-send-${quote.id}`}>
                  Send request
                </Button>
                <Button onClick={() => setShowRequest(false)} variant="outline" className="rounded-full h-10 px-5">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {quote.status === "booked" && quote.booking && (
        <div className="mt-6 rounded-xl border border-[#3C5A3F] bg-[#E3EBDA] p-5" data-testid="booked-block">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#3C5A3F]">Booking confirmed</p>
          <p className="mt-1 font-[Outfit] text-xl font-semibold text-[#2C3E35]">
            {formatDate(quote.booking.date)} · <span className="capitalize">{quote.booking.slot}</span>
          </p>
          <p className="mt-1 text-sm text-[#3C5A3F]">We'll see you then. Thanks for choosing Honeystone.</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold tracking-wide uppercase text-[#73777B]">{label}</p>
      <p className="mt-0.5 text-sm text-[#2C3E35]">{value || "—"}</p>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}
